import { Pencil, RefreshCw } from "lucide-react";
import { useMemo, useState } from "react";
import Markdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import remarkBreaks from "remark-breaks";
import remarkGfm from "remark-gfm";
import type { Language, ReportSection } from "~/api/model";
import { ContentCopyButton } from "~/components/ContentCopyButton";
import { LoadingSpinner } from "~/components/Icons";
import { Button } from "~/components/ui/button";
import {
  TERMS_PROSE_CLASS,
  getTermsMarkdownComponents,
  highlightTermsInMarkdown,
  unwrapAllCodeBlocks,
} from "~/utils/markdownTerms";
import { Hint } from "../../types";
import { EditReportSectionDialog } from "./EditReportSectionDialog";
import { localizeReportSectionTitle } from "./localizeReportSectionTitle";
import { ReportViewStatus } from "./ReportView";

export const ReportSectionView = ({
  reportSection,
  terms,
  onRegenerate,
  onEdit,
  isRegenerating,
  status,
  templateName,
  outputLanguage,
  isActionAvailable = true,
}: {
  reportSection: ReportSection;
  terms: Hint[];
  onRegenerate: () => void;
  onEdit: (content: string) => Promise<void>;
  isRegenerating: boolean;
  status: ReportViewStatus;
  templateName?: string | null;
  outputLanguage?: Language | null;
  isActionAvailable?: boolean;
}) => {
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  const displayTitle = localizeReportSectionTitle(
    reportSection.title,
    templateName,
    outputLanguage
  );

  const highlightedContent = useMemo(() => {
    if (!reportSection.content) return "";
    const preprocessed = unwrapAllCodeBlocks(reportSection.content);
    return highlightTermsInMarkdown(preprocessed, terms);
  }, [reportSection.content, terms]);

  return (
    <div className="group">
      <h2 className="text-sm font-semibold mb-2">{displayTitle}</h2>
      <Markdown
        remarkPlugins={[remarkGfm, remarkBreaks]}
        rehypePlugins={[rehypeRaw]}
        className={TERMS_PROSE_CLASS}
        components={{
          ...getTermsMarkdownComponents(terms),
        }}
      >
        {highlightedContent}
      </Markdown>
      <div className="h-4 invisible opacity-0 group-hover:visible group-hover:opacity-100 transition-opacity duration-200 mt-0">
        <div className="inline-flex items-center gap-2">
          <ContentCopyButton
            content={reportSection.content ?? ""}
            preCopyMessageId="copy"
            color="muted-foreground"
          />
          {isActionAvailable && (
            <Button
              variant="ghost"
              size="mini"
              className="text-muted-foreground hover:text-muted-foreground inline-flex items-center px-0 text-xs hover:bg-transparent"
              onClick={() => setEditDialogOpen(true)}
              disabled={isRegenerating}
            >
              <Pencil className="w-4 h-4 mr-1" />
              Edit
            </Button>
          )}
          {isActionAvailable && (
            <Button
              variant="ghost"
              size="mini"
              className="text-muted-foreground hover:text-muted-foreground inline-flex items-center px-0 text-xs hover:bg-transparent"
              onClick={onRegenerate}
              disabled={
                isRegenerating ||
                status.type === "outdated" ||
                status.type === "templateDeleted" ||
                status.type === "pending"
              }
            >
              {isRegenerating ? (
                <LoadingSpinner className="w-4 h-4 mr-1" />
              ) : (
                <RefreshCw className="w-4 h-4 mr-1" />
              )}
              Rewrite
            </Button>
          )}
        </div>
      </div>

      <EditReportSectionDialog
        section={reportSection}
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        onSave={onEdit}
      />
    </div>
  );
};
